import { useNavigation } from '@app/hooks/useNavigation';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { useGetCompanyChatsQuery } from '@dieterApi/companyChat/hooks';
import { useDpoTaskSetApprovalMutation } from '@dieterApi/dpo/useDpoTaskSetApprovalMutation';
import { useDpoTaskSetEnabledMutation } from '@dieterApi/dpo/useDpoTaskSetEnabledMutation';
import { DpoState, DpoTaskResourceType, DpoTaskState, useDpoTasksQuery } from '@dieterApi/dpo/useDpoTasksQuery';
import { useTopicsQuery } from '@dieterApi/topic/useTopicsQuery';
import { useWhitelabelConfigQuery } from '@dieterApi/whitelabel/useWhitelabelConfigQuery';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AttachmentIcon from '@mui/icons-material/Attachment';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { DeadlineBadge } from '@components/ui';
import { Badge, IconButton, Switch, Tooltip } from '@mui/material';
import { ListIcon } from 'lucide-react';
import MUIDataTable, { MUIDataTableColumn, MUIDataTableMeta, MUIDataTableOptions } from 'mui-datatables';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionLogModal } from './ActionLogModal';
import { DocumentComparer } from './DocumentComparer';

// TODO: i18n translation
const STATE_LABELS: Record<DpoState, string> = {
  [DpoState.UNTOUCHED]: 'Nicht begonnen',
  [DpoState.TOUCHED]: 'In Bearbeitung',
  [DpoState.COMPLETE]: 'Fertig (nicht abgenommen)',
  [DpoState.AUDITED]: 'Abgenommen',
};

function StatusBadge({
  state,
  dangerColor,
  warningColor,
  successColor,
}: {
  state: DpoState;
  dangerColor?: string;
  warningColor?: string;
  successColor?: string;
}) {
  const getStateColor = (): string => {
    switch (state) {
      case DpoState.UNTOUCHED:
        return '#BBBBBB'; // Gray: Not started
      case DpoState.TOUCHED:
        return dangerColor || '#FF759E'; // Red: In progress
      case DpoState.COMPLETE:
        return warningColor || '#FDD213'; // Yellow: Done by user, awaiting DPO
      case DpoState.AUDITED:
        return successColor || '#91AB46'; // Green: Approved by DPO
      default:
        return '#BBBBBB';
    }
  };

  return (
    <Tooltip title={STATE_LABELS[state]}>
      <span className="inline-block w-4 h-4 rounded-full" style={{ backgroundColor: getStateColor() }} />
    </Tooltip>
  );
}

interface FlattenedTask extends DpoTaskState {
  isSubItem?: boolean;
  parentId?: string;
  isSubtopicContainer?: boolean;
  subtopicTitle?: string;
  sortGroup?: string;
  sortOrder?: number;
}

export function DpoTable() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user, activeCompany } = useUserWithCompany();
  const { navigation } = useNavigation();
  const companyId = activeCompany?.id;

  const { data, loading, error } = useDpoTasksQuery(companyId);
  const { data: topicsData } = useTopicsQuery(companyId || '');
  const topics = topicsData?.getTopics || [];
  const { data: chatsData } = useGetCompanyChatsQuery();
  const { data: whitelabelData } = useWhitelabelConfigQuery();
  const whitelabelConfig = whitelabelData?.whitelabelConfig;
  const [setEnabled, { loading: mutationLoading }] = useDpoTaskSetEnabledMutation(companyId || '');
  const [approveTask, { loading: approveLoading }] = useDpoTaskSetApprovalMutation(companyId || '');

  const allTasks = data?.getDpoTasks || [];
  // Filter out experimental and meta-tree topics
  const tasks = allTasks.filter((task) => task.topicId !== 'topic-experimental' && task.topicId !== 'topic-meta');

  // Helper function to check if a task has unread messages
  const hasUnreadMessages = (taskId: string): boolean => {
    if (!chatsData?.getCompanyChats || !companyId) return false;

    // Find chat associated with this task by title pattern
    const chatTitlePattern = `DPO Task: ${taskId}`;
    const chat = chatsData.getCompanyChats.find(
      (c: any) => c.companyId === companyId && c.title?.includes(chatTitlePattern)
    );

    if (!chat || !chat.messages || chat.messages.length === 0) return false;

    // Get the last message in the chat
    const lastMessage = chat.messages[chat.messages.length - 1];

    // Check if we've read this message
    const lastReadMessageId = navigation.chatLastReadMessageIds?.[chat.id];

    // If we haven't read any messages in this chat, or the last message is different, it's unread
    return !lastReadMessageId || lastReadMessageId !== lastMessage.id;
  };

  const [showDocumentComparer, setShowDocumentComparer] = useState(false);
  const [showActionLogModal, setShowActionLogModal] = useState(false);
  const [selectedActionLogTask, setSelectedActionLogTask] = useState<{ id: string; name: string } | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedTableMeta, setSelectedTableMeta] = useState<MUIDataTableMeta | null>(null);
  const [selectedHasFile, setSelectedHasFile] = useState<boolean>(false);
  const [enabledOverrides, setEnabledOverrides] = useState<Record<string, boolean>>({});
  const [sortColumn, setSortColumn] = useState<string>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showScrollShadow, setShowScrollShadow] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const headerPaddingStyle = { padding: '0px 0px 0px 8px' };
  const textCellPaddingStyle = { padding: '4px 2px 4px 4px' };
  const iconCellPaddingStyle = { padding: '0px 2px 0px 0px' };
  const iconSize = 24;

  const switchStyles = {
    width: 34,
    height: 16,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: '2px',
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(18px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#808080',
          opacity: 1,
          border: 0,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 12,
      height: 12,
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.2)',
      color: '#fff',
    },
    '& .MuiSwitch-track': {
      borderRadius: 20 / 2,
      backgroundColor: '#BFBFBF',
      opacity: 1,
      transition: 'background-color 300ms',
    },
  };

  // Separate questionnaires and list items
  const questionnaireTasks = tasks.filter((t) => t.type === DpoTaskResourceType.Questionnaire);
  const listItemTasks = tasks.filter((t) => t.type === DpoTaskResourceType.ListItem);

  // Group list items by their listCategory (which maps to subtopic IDs)
  const listItemsByCategory = new Map<string, DpoTaskState[]>();
  listItemTasks.forEach((listItem) => {
    if (listItem.listCategory) {
      if (!listItemsByCategory.has(listItem.listCategory)) {
        listItemsByCategory.set(listItem.listCategory, []);
      }
      listItemsByCategory.get(listItem.listCategory)!.push(listItem);
    }
  });

  // Display name mapping for subtopic containers in DPO table
  const subtopicDisplayNames: Record<string, string> = {
    'dsv-incidents': 'Datenschutzverletzungen',
    'av-contract-partners': 'Vertragspartner',
    'rights-request': 'Betroffenenanfragen',
    'info-forms': 'Datenschutzhinweise',
    'info-consent-forms': 'Einwilligungserklärungen',
    'dsfa-entries': 'DSFA Einträge',
  };

  // Create subtopic container entries from topics with subItemLocales
  const subtopicContainers: FlattenedTask[] = [];
  topics.forEach((topic) => {
    topic.subItemLocales?.forEach((subItem) => {
      const childItems = listItemsByCategory.get(subItem.id) || [];
      if (childItems.length > 0) {
        const displayName = subtopicDisplayNames[subItem.id] || subItem.title;

        // Calculate the latest updatedAt from all child items
        const latestUpdatedAt = childItems.reduce<string | undefined>((latest, child) => {
          if (!child.updatedAt) return latest;
          if (!latest) return child.updatedAt;
          return new Date(child.updatedAt) > new Date(latest) ? child.updatedAt : latest;
        }, undefined);

        subtopicContainers.push({
          id: `subtopic-${subItem.id}`,
          name: displayName,
          topicId: topic.id,
          topicTitle: topic.titleLocales.de,
          dpoState: DpoState.UNTOUCHED,
          type: DpoTaskResourceType.ListItem,
          hasFile: false,
          isEnabled: true,
          isApproved: false,
          isSubtopicContainer: true,
          subtopicTitle: displayName,
          listCategory: subItem.id,
          updatedAt: latestUpdatedAt,
        });
      }
    });
  });

  // Flatten data for table display
  const flattenedData: FlattenedTask[] = [];

  // Add questionnaires as standalone top-level rows
  questionnaireTasks.forEach((q) => {
    flattenedData.push({
      ...q,
      sortGroup: q.id,
      sortOrder: 0,
    });
  });

  // Add subtopic containers with their children
  subtopicContainers.forEach((container) => {
    flattenedData.push({
      ...container,
      sortGroup: container.id,
      sortOrder: 0,
    });
    if (expandedRows.has(container.id)) {
      const children = listItemsByCategory.get(container.listCategory!) || [];
      children.forEach((child, index) => {
        flattenedData.push({
          ...child,
          isSubItem: true,
          parentId: container.id,
          sortGroup: container.id,
          sortOrder: index + 1,
        });
      });
    }
  });

  // ListItems are grouped by topicId matching
  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  useEffect(() => {
    const findScrollableElement = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null;

      // Check all descendants
      const allElements = element.querySelectorAll('*');
      for (let i = 0; i < allElements.length; i++) {
        const el = allElements[i] as HTMLElement;
        const style = window.getComputedStyle(el);
        const overflowX = style.overflowX;

        // Only consider elements with overflow-x: auto or scroll AND that actually scroll
        if ((overflowX === 'auto' || overflowX === 'scroll') && el.scrollWidth > el.clientWidth) {
          return el;
        }
      }
      return null;
    };

    const checkScrollPosition = (scrollContainer: HTMLElement) => {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
      const isScrollable = scrollWidth > clientWidth;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;
      setShowScrollShadow(isScrollable && !isAtEnd);
    };

    // Wait for table to render
    const timer = setTimeout(() => {
      const scrollContainer = findScrollableElement(tableContainerRef.current);

      if (scrollContainer) {
        const handler = () => checkScrollPosition(scrollContainer);
        scrollContainer.addEventListener('scroll', handler);
        checkScrollPosition(scrollContainer);

        // Store for cleanup
        (tableContainerRef.current as any)._scrollContainer = scrollContainer;
        (tableContainerRef.current as any)._scrollHandler = handler;
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      if ((tableContainerRef.current as any)?._scrollContainer) {
        (tableContainerRef.current as any)._scrollContainer.removeEventListener(
          'scroll',
          (tableContainerRef.current as any)._scrollHandler
        );
      }
    };
  }, [flattenedData]);

  const handleToggle = async (taskId: string, currentEnabled: boolean) => {
    if (!companyId) {
      console.log('No company ID available');
      return;
    }
    // Optimistic UI update
    setEnabledOverrides((prev) => ({ ...prev, [taskId]: !currentEnabled }));

    try {
      await setEnabled({
        variables: {
          companyId,
          taskId,
          enabled: !currentEnabled,
        },
      });
      // Keep optimistic state; server/cache should catch up. If needed, we can clear override later.
    } catch (err) {
      // Rollback and notify on failure
      setEnabledOverrides((prev) => {
        const { [taskId]: _, ...rest } = prev;
        return rest;
      });
      enqueueSnackbar(
        (t('dashboard.dpotable.toggle_failed') as string) || 'Änderung konnte nicht gespeichert werden. Zurückgesetzt.',
        { variant: 'error' }
      );
    }
  };

  const handleToggleAll = async (enabled: boolean) => {
    if (!companyId) return;

    // Get tasks that need to be toggled
    const tasksToToggle = tasks.filter((task) => task.isEnabled !== enabled);

    // Optimistic UI update for all tasks at once
    const optimisticUpdates: Record<string, boolean> = {};
    tasksToToggle.forEach((task) => {
      optimisticUpdates[task.id] = enabled;
    });
    setEnabledOverrides((prev) => ({ ...prev, ...optimisticUpdates }));

    // Execute all mutations in parallel
    const results = await Promise.allSettled(
      tasksToToggle.map((task) =>
        setEnabled({
          variables: {
            companyId,
            taskId: task.id,
            enabled,
          },
        })
          .then(() => ({ taskId: task.id, success: true }))
          .catch(() => ({ taskId: task.id, success: false }))
      )
    );

    // Rollback only the failed tasks
    const failedTaskIds = results
      .filter((result) => result.status === 'fulfilled' && !result.value.success)
      .map((result) => (result.status === 'fulfilled' ? result.value.taskId : ''));

    if (failedTaskIds.length > 0) {
      setEnabledOverrides((prev) => {
        const newOverrides = { ...prev };
        failedTaskIds.forEach((taskId) => {
          delete newOverrides[taskId];
        });
        return newOverrides;
      });
      enqueueSnackbar(
        (t('dashboard.dpotable.toggle_some_failed') as string) ||
          `${failedTaskIds.length} Änderung(en) konnten nicht gespeichert werden.`,
        { variant: 'warning' }
      );
    }
  };

  const handleEditDpo = (tableMeta: MUIDataTableMeta) => {
    setSelectedTableMeta(tableMeta);
    setShowDocumentComparer(true);
  };

  const handleGenerateReport = (tableMeta: MUIDataTableMeta) => {
    const taskId = getColValue(tableMeta.rowData, 'id') as string;
    const taskName = getColValue(tableMeta.rowData, 'name') as string;
    const questionnaireId = getColValue(tableMeta.rowData, 'questionnaireId') as string | undefined;

    // Use questionnaireId if available (for questionnaires), otherwise use taskId (for list items)
    const resourceId = questionnaireId || taskId;

    setSelectedActionLogTask({ id: resourceId, name: taskName });
    setShowActionLogModal(true);
  };

  const columns: MUIDataTableColumn[] = [
    {
      name: 'id',
      label: 'ID',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'isEnabled',
      label: t('dashboard.dpotable.is_enabled') || 'Toggle',
      options: {
        customHeadLabelRender: () => '',
        setCellHeaderProps: () => ({ style: { ...headerPaddingStyle, textAlign: 'center' } }),
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRender: (value, tableMeta) => {
          const taskId = getColValue(tableMeta.rowData, 'id') as string;
          const isSubItem = getColValue(tableMeta.rowData, 'isSubItem');
          const isSubtopicContainer = getColValue(tableMeta.rowData, 'isSubtopicContainer');

          if (isSubItem || isSubtopicContainer) {
            return null;
          }

          return (
            <Switch
              checked={taskId in enabledOverrides ? enabledOverrides[taskId] : Boolean(value)}
              onChange={() =>
                handleToggle(taskId, taskId in enabledOverrides ? enabledOverrides[taskId] : Boolean(value))
              }
              disabled={mutationLoading}
              sx={switchStyles}
            />
          );
        },
      },
    },
    {
      name: 'name',
      label: t('dashboard.dpotable.name') || 'Name',
      options: {
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const taskId = getColValue(tableMeta.rowData, 'id');
          const isSubItem = getColValue(tableMeta.rowData, 'isSubItem');
          const isSubtopicContainer = getColValue(tableMeta.rowData, 'isSubtopicContainer');
          const categoryId = getColValue(tableMeta.rowData, 'listCategory');

          // Check if this subtopic container has children
          const hasSubItems =
            isSubtopicContainer &&
            categoryId &&
            listItemsByCategory.has(categoryId) &&
            (listItemsByCategory.get(categoryId)?.length || 0) > 0;
          const isExpanded = expandedRows.has(taskId);

          const iconButtonWidth = 28;

          return (
            <div style={{ display: 'flex', alignItems: 'center', paddingLeft: isSubItem ? '12px' : '0px' }}>
              {!isSubItem && (
                <div
                  style={{
                    minWidth: `${iconButtonWidth}px`,
                    marginRight: '1px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {hasSubItems && (
                    <IconButton size="small" onClick={() => toggleExpand(taskId)} sx={{ padding: '1px' }}>
                      {isExpanded ? (
                        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  )}
                </div>
              )}
              <span style={{ fontWeight: isSubtopicContainer ? 500 : 'normal' }}>{value}</span>
              {getColValue(tableMeta.rowData, 'deadlineStatus') && (
                <span style={{ marginLeft: '8px' }}>
                  <DeadlineBadge
                    status={getColValue(tableMeta.rowData, 'deadlineStatus')}
                    deadline={getColValue(tableMeta.rowData, 'deadline')}
                  />
                </span>
              )}
            </div>
          );
        },
        setCellProps: () => ({
          style: { borderLeft: '1px solid #E3E3E3', ...textCellPaddingStyle },
        }),
        setCellHeaderProps: () => ({ style: headerPaddingStyle }),
      },
    },
    {
      name: 'dpoState',
      label: t('dashboard.dpotable.status') || 'Status',
      options: {
        sort: true,
        customBodyRender: (value: DpoState) => (
          <StatusBadge
            state={value}
            dangerColor={whitelabelConfig?.dangerColor}
            warningColor={whitelabelConfig?.warningColor}
            successColor={whitelabelConfig?.successColor}
          />
        ),
        setCellHeaderProps: () => ({ style: { ...headerPaddingStyle, textAlign: 'center' } }),
        setCellProps: () => ({ style: { textAlign: 'center' } }),
      },
    },
    {
      name: 'topicTitle',
      label: t('dashboard.dpotable.topic') || 'Topic',
      options: {
        sort: true,
        customBodyRender: (value) => value,
        setCellHeaderProps: () => ({ style: headerPaddingStyle }),
        setCellProps: () => ({ style: textCellPaddingStyle }),
      },
    },
    {
      name: 'updatedAt',
      label: t('dashboard.dpotable.last_change') || 'Last Change',
      options: {
        sort: true,
        customBodyRender: (value: string | undefined) => {
          if (!value) return '-';
          return new Date(value).toLocaleDateString('de-DE');
        },
        customHeadLabelRender: () => (
          <div style={{ textAlign: 'left', width: '100%' }}>{t('dashboard.dpotable.last_change') || 'Last Change'}</div>
        ),
        setCellHeaderProps: () => ({ style: headerPaddingStyle }),
        setCellProps: () => ({ style: textCellPaddingStyle }),
      },
    },
    {
      name: 'hasFile',
      label: t('dashboard.dpotable.file') || 'File',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'edit',
      label: t('dashboard.dpotable.edit') || 'Edit',
      options: {
        customHeadLabelRender: () => '',
        setCellHeaderProps: () => ({ style: { ...headerPaddingStyle, textAlign: 'center' } }),
        setCellProps: () => ({ style: { ...iconCellPaddingStyle, textAlign: 'center', paddingRight: '12px' } }),
        customBodyRender: (value, tableMeta) => {
          const hasFile = getColValue(tableMeta.rowData, 'hasFile') as boolean;
          const dpoState = getColValue(tableMeta.rowData, 'dpoState') as DpoState;
          const isSubtopicContainer = getColValue(tableMeta.rowData, 'isSubtopicContainer');

          // Don't show edit button for subtopic containers
          if (isSubtopicContainer) {
            return null;
          }

          // Only show edit button when task is complete or audited (yellow or green)
          const canEdit = dpoState === DpoState.COMPLETE || dpoState === DpoState.AUDITED;

          if (!canEdit) {
            return null;
          }

          return (
            <Tooltip title={t('dashboard.dpotable.edit') || 'Edit'}>
              <Badge
                invisible={!hasFile}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                badgeContent={
                  <AttachmentIcon
                    sx={{
                      fontSize: 18,
                      transform: 'rotate(-45deg)',
                      color: '#666',
                    }}
                  />
                }
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: 'transparent',
                    padding: 0,
                    minWidth: 0,
                  },
                }}
              >
                <IconButton size="small" onClick={() => handleEditDpo(tableMeta)}>
                  <EditOutlinedIcon sx={{ fontSize: iconSize, color: '#999' }} />
                </IconButton>
              </Badge>
            </Tooltip>
          );
        },
      },
    },
    {
      name: 'report',
      label: t('dashboard.dpotable.report') || 'Report',
      options: {
        customBodyRender: (value, tableMeta) => {
          const isSubtopicContainer = getColValue(tableMeta.rowData, 'isSubtopicContainer');

          // Don't show report button for subtopic containers
          if (isSubtopicContainer) {
            return null;
          }

          return (
            <IconButton size="small" onClick={() => handleGenerateReport(tableMeta)}>
              <AssignmentOutlinedIcon sx={{ fontSize: iconSize, color: '#999' }} />
            </IconButton>
          );
        },
        setCellProps: () => ({ style: { borderLeft: '1px solid #E3E3E3', textAlign: 'center' } }),
        setCellHeaderProps: () => ({
          style: { ...headerPaddingStyle, borderLeft: '1px solid #E3E3E3', textAlign: 'center' },
        }),
      },
    },
    {
      name: 'chat',
      label: 'Chat',
      options: {
        customHeadLabelRender: () => '',
        setCellHeaderProps: () => ({ style: { ...headerPaddingStyle, textAlign: 'center' } }),
        setCellProps: () => ({ style: { ...iconCellPaddingStyle, textAlign: 'center', paddingRight: '12px' } }),
        customBodyRender: (value, tableMeta) => {
          const taskId = getColValue(tableMeta.rowData, 'id') as string;
          const isSubtopicContainer = getColValue(tableMeta.rowData, 'isSubtopicContainer');

          // Don't show chat indicator for subtopic containers
          if (isSubtopicContainer) {
            return null;
          }

          const hasUnread = hasUnreadMessages(taskId);

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {hasUnread && (
                <Tooltip title="Neue Chatnachricht">
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#FF759E',
                      display: 'inline-block',
                    }}
                  />
                </Tooltip>
              )}
            </div>
          );
        },
      },
    },
    {
      name: 'isSubItem',
      label: 'isSubItem',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'parentId',
      label: 'parentId',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'isSubtopicContainer',
      label: 'isSubtopicContainer',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'subtopicTitle',
      label: 'subtopicTitle',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'listCategory',
      label: 'listCategory',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'deadline',
      label: 'deadline',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'deadlineStatus',
      label: 'deadlineStatus',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'sortGroup',
      label: 'sortGroup',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'sortOrder',
      label: 'sortOrder',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'questionnaireId',
      label: 'questionnaireId',
      options: {
        display: 'excluded',
      },
    },
  ];

  // Helper to get column value by name instead of hardcoded index
  const getColValue = (rowData: any[], colName: string) => {
    const colIndex = columns.findIndex((c) => c.name === colName);
    return colIndex >= 0 ? rowData[colIndex] : undefined;
  };

  const options: MUIDataTableOptions = {
    download: false,
    print: false,
    elevation: 0,
    selectableRows: 'none',
    selectableRowsHeader: false,
    search: false,
    filter: false,
    viewColumns: false,
    pagination: true,
    rowsPerPage: 50,
    rowsPerPageOptions: [10, 25, 50],
    sortOrder: { name: sortColumn, direction: sortDirection },
    onColumnSortChange: (changedColumn: string, direction: 'asc' | 'desc') => {
      setSortColumn(changedColumn);
      setSortDirection(direction);
    },
    customSort: (data, colIndex, order) => {
      const sortGroupIndex = columns.findIndex((c) => c.name === 'sortGroup');
      const sortOrderIndex = columns.findIndex((c) => c.name === 'sortOrder');

      return data.sort((a, b) => {
        // Get sortGroup and sortOrder for both rows
        const sortGroupA = a.data[sortGroupIndex];
        const sortGroupB = b.data[sortGroupIndex];
        const sortOrderA = a.data[sortOrderIndex];
        const sortOrderB = b.data[sortOrderIndex];

        // If they're in the same group, sort by sortOrder to maintain hierarchy
        if (sortGroupA === sortGroupB) {
          return sortOrderA - sortOrderB;
        }

        // Different groups - need to compare by the sort column value
        // Find the parent row (sortOrder = 0) for each group to get the sort value
        const parentA = data.find((row) => row.data[sortGroupIndex] === sortGroupA && row.data[sortOrderIndex] === 0);
        const parentB = data.find((row) => row.data[sortGroupIndex] === sortGroupB && row.data[sortOrderIndex] === 0);

        const valA = parentA ? parentA.data[colIndex] : a.data[colIndex];
        const valB = parentB ? parentB.data[colIndex] : b.data[colIndex];

        // Handle null/undefined values
        if (valA == null && valB == null) return 0;
        if (valA == null) return order === 'asc' ? 1 : -1;
        if (valB == null) return order === 'asc' ? -1 : 1;

        // Compare values
        let comparison = 0;
        if (typeof valA === 'string' && typeof valB === 'string') {
          comparison = valA.localeCompare(valB);
        } else if (valA instanceof Date && valB instanceof Date) {
          comparison = valA.getTime() - valB.getTime();
        } else {
          comparison = valA < valB ? -1 : valA > valB ? 1 : 0;
        }

        return order === 'desc' ? -comparison : comparison;
      });
    },
    setRowProps: (row, dataIndex) => {
      return {
        style: {
          ...(dataIndex === 0 ? { borderTop: '2px solid #E3E3E3' } : {}),
        },
      };
    },
    textLabels: {
      body: {
        noMatch: t('dashboard.dpotable.nomatch') || 'No entries found',
      },
    },
  };

  if (loading) {
    return (
      <div className="dtDSBTable" style={{ border: '2px solid #E3E3E3', borderRadius: '8px' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #E3E3E3' }}>
          <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ListIcon size={24} />
            {t('dashboard.dpotable.title') || 'Documents'}
          </p>
          <p className="text-gray-500 mt-4">{t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dtDSBTable" style={{ border: '2px solid #E3E3E3', borderRadius: '8px' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #E3E3E3' }}>
          <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ListIcon size={24} />
            {t('dashboard.dpotable.title') || 'Documents'}
          </p>
          <p className="text-red-500 mt-4">
            Fehler beim Laden: {error.message}
          </p>
        </div>
      </div>
    );
  }

  if (!user?.hasFeature('DpoManaged')) {
    return (
      <div className="2xl:container p-6">
        <h3 className="text-xl font-medium">DPO Aufgabenliste</h3>
        <p className="text-gray-500 mt-4">DPO-Modus ist für dieses Unternehmen nicht aktiviert.</p>
      </div>
    );
  }

  const allEnabled = tasks.every((t) => t.isEnabled);

  return (
    <div className="dtDSBTable" style={{ position: 'relative' }} ref={tableContainerRef}>
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '20px',
          background: 'linear-gradient(to left, rgba(0,0,0,0.08), rgba(0,0,0,0))',
          pointerEvents: 'none',
          zIndex: 1,
          opacity: showScrollShadow ? 1 : 0,
          transition: 'opacity 0.6s ease-in-out',
        }}
      />
      <div style={{ borderBottom: '1px solid #E3E3E3' }}>
        <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
          <ListIcon size={20} />
          {t('dashboard.dpotable.title') || 'Documents'}
          <Switch
            checked={allEnabled}
            onChange={(e) => handleToggleAll(e.target.checked)}
            disabled={mutationLoading}
            sx={switchStyles}
          />
          <span style={{ fontSize: '11px', color: '#909090' }}>
            {t('dashboard.dpotable.enable_all') || 'Enable all'}
          </span>
        </p>
        {flattenedData && <MUIDataTable columns={columns} data={flattenedData} title="" options={options} />}
      </div>

      {showDocumentComparer && (
        <DocumentComparer onClose={() => setShowDocumentComparer(false)} tableMeta={selectedTableMeta} />
      )}

      {showActionLogModal && selectedActionLogTask && (
        <ActionLogModal
          onClose={() => setShowActionLogModal(false)}
          resourceId={selectedActionLogTask.id}
          taskName={selectedActionLogTask.name}
        />
      )}
    </div>
  );
}
